/**
 * Card de um item do portfólio.
 *
 * Formato horizontal do esboço: monograma à esquerda, corpo à direita com nome,
 * descrição de uma linha e a pastilha de estado. Compacto de propósito — cabem
 * quatro por linha, e o portfólio inteiro numa tela só.
 *
 * ## Por que o card deixou de ser um `<button>`
 *
 * Era: o card inteiro era um `<button>` e o clique abria o painel. Com os
 * atalhos de abrir direto — o mesmo agente no Teams e no Microsoft 365 — passou
 * a haver `<a>` dentro dele, e **conteúdo interativo dentro de `<button>` é HTML
 * inválido**: o navegador desfaz o aninhamento, e o resultado varia entre eles.
 *
 * A saída é o *stretched link*: o card é um `<article>` posicionado, o botão do
 * título estica um `::after` transparente sobre a área toda, e os atalhos ficam
 * acima dele por `z-index`. O card continua clicável inteiro, os atalhos
 * continuam links de verdade — abrem em nova aba, aceitam "abrir em nova
 * janela" e o botão do meio funciona.
 *
 * Nenhum `style={{...}}` — a CSP tem `style-src 'self'` e o atributo gerado é
 * bloqueado em silêncio. Estado vira classe.
 */
import { logoUrl } from '../api.js';
import { DestinationIcon, destinationsOf } from './DestinationIcon.jsx';

/**
 * Pastilha de estado.
 *
 * Duas informações diferentes disputam o mesmo espaço, e a ordem importa: o que
 * a pessoa PODE FAZER vence o que o item É. Quem já tem acesso precisa ver
 * "Liberado", não "Ativo" — "Ativo" descreve o item e não responde à pergunta
 * que ela está fazendo ao olhar o card.
 */
function statusPill(item) {
  const { access, status } = item;

  if (status === 'COMING_SOON') return { text: 'Em breve', cls: 'pending' };
  if (status === 'INACTIVE') return { text: 'Indisponível', cls: 'neutral' };
  if (status === 'OUT_OF_MATRIX') return { text: 'Fora da matriz de acesso', cls: 'neutral' };

  if (access.decision === 'ALLOWED') return { text: 'Liberado', cls: 'ok' };
  if (access.decision === 'REQUEST') return { text: 'Sem acesso', cls: 'info' };
  return { text: 'Indisponível', cls: 'neutral' };
}

/**
 * Quantos atalhos cabem no card antes de virar poluição.
 *
 * Três é o que cabe ao lado da pastilha sem quebrar linha na coluna mais
 * estreita do grid. Quem tem mais destinos vê todos no painel de detalhe, que
 * é para onde o card leva de qualquer forma.
 */
const MAX_SHORTCUTS = 3;

/**
 * `primeiro` e as marcas `data-tour-*` existem para o tour guiado.
 *
 * O motor lê o DOM para decidir quais passos fazem sentido AGORA: sem cartão
 * nenhum, o passo que fala de cartão é pulado; sem item em "Solicitar acesso",
 * o passo que ensina a pedir é pulado — porque mandar clicar num botão que não
 * existe na tela de quem lê é instrução falsa.
 *
 * Marcar só o PRIMEIRO cartão é deliberado: `querySelector` pega o primeiro de
 * qualquer jeito, e marcar os vinte faria parecer que a âncora é "todo cartão"
 * para quem for mexer depois.
 */
export function ItemCard({ item, onOpen, primeiro = false }) {
  const p = statusPill(item);
  const inert = item.status === 'INACTIVE';
  const shortcuts = destinationsOf(item).slice(0, MAX_SHORTCUTS);

  return (
    <article
      className={`item-card ${inert ? 'inert' : ''}`}
      data-tour={primeiro ? 'catalogo-cartao' : undefined}
      data-tour-solicitavel={item.access?.canRequest ? 'sim' : undefined}
    >
      {/* Iniciais sempre no fluxo, logotipo por cima quando existe: e o que
          evita o quadrado vazio piscando enquanto a imagem carrega, e o salto
          de layout quando ela nao existe. Mesmo desenho do `Avatar`. */}
      <span className="card-avatar" aria-hidden="true">
        <span className="mark-initials">
          {item.initials ?? item.name.slice(0, 2).toUpperCase()}
        </span>
        {item.hasLogo && (
          <img
            className="mark-logo"
            src={logoUrl(item.id, item.logoUpdatedAt)}
            alt=""
            loading="lazy"
          />
        )}
      </span>

      <span className="card-body">
        <span className="card-title-row">
          <button
            type="button"
            className="card-hit"
            onClick={() => onOpen(item)}
            aria-label={`${item.name} — ${p.text}. Ver detalhes`}
          >
            <span className="card-title">{item.name}</span>
          </button>
          <span className="card-caret" aria-hidden="true">
            ›
          </span>
        </span>

        {item.description && <span className="card-desc">{item.description}</span>}

        <span className="card-foot">
          <span className={`status-pill ${p.cls}`}>{p.text}</span>

          {shortcuts.length > 0 && (
            <span className="card-opens">
              {shortcuts.map((d) => (
                // `noopener` obrigatório: sem ele a página de destino recebe
                // `window.opener` e pode reescrever a nossa aba.
                <a
                  key={d.url}
                  className="open-chip"
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Abrir no ${d.where}`}
                  aria-label={`${item.name}: abrir no ${d.where}`}
                >
                  <DestinationIcon url={d.url} />
                </a>
              ))}
            </span>
          )}
        </span>
      </span>
    </article>
  );
}

export default ItemCard;
